import { Fragment } from "react";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormikProps } from "formik";
import { DateTime } from "luxon";
import { t } from "i18next";

import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { TableLoader } from "../molecules/TableLoader";
import ColumnText from "../molecules/ColumnText";
import { Empty } from "./Empty";

export type TransactionsTableProps = {
  isLoading: boolean;
  transactions: any[];
  onDelete: (transaction: any) => void;
  onDownloadTicket: (transaction: any) => void;
  onDownloadInvoice: (transaction: any) => void;
  onNewTransaction: () => void;
  formik: FormikProps<any>;
};

export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  isLoading,
  transactions,
  onDownloadInvoice,
  onDownloadTicket,
  onDelete,
  onNewTransaction,
  formik,
}) => {
  return (
    <div className="flex-1 overflow-scroll flex flex-col border">
      <div className="flex flex-col">
        {isLoading ? (
          <TableLoader columns={9} />
        ) : (
          <Fragment>
            {transactions.length === 0 ? (
              <Empty
                texto={t("transaction.views.no_transactions")}
                onClick={onNewTransaction}
              />
            ) : (
              <Fragment>
                <table className="divide-y divide-gray-300">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("general.date")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("general.category")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("pages.accounting.product")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("general.description")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("general.comments")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("pages.accounting.sold_by")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("pages.accounting.purchased_by")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("general.amount")}
                      </th>
                      {formik.values.branchIds.length > 1 && (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t("accounting.views.branch")}
                        </th>
                      )}
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {" "}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {transactions.map((transaction: any) => (
                      <tr key={transaction.id}>
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                          {DateTime.fromFormat(
                            transaction.date,
                            "yyyy-MM-dd"
                          ).toFormat("dd-MM-yyyy")}
                        </td>
                        <ColumnText
                          wrap
                          title={t(
                            `accounting.views.transaction_type.${transaction.type}`
                          )}
                        />
                        <ColumnText wrap title={transaction.itemName} />
                        <ColumnText wrap title={transaction.itemDescription} />
                        <ColumnText wrap title={transaction.comment} />
                        <ColumnText
                          wrap
                          title={transaction.chargedByUser?.name}
                        />
                        <ColumnText wrap title={transaction.purchasedByName} />
                        <ColumnText
                          title={`${transaction.currency} ${parseFloat(transaction.amount).toFixed(2)}`}
                        />
                        {formik.values.branchIds.length > 1 && (
                          <ColumnText wrap title={transaction.branchName} />
                        )}
                        <ColumnText
                          wrap
                          title={
                            <div className="flex gap-2 items-center">
                              {false && (
                                <ButtonSecondary
                                  text={t("accounting.views.invoice")}
                                  onClick={() => onDownloadInvoice(transaction)}
                                />
                              )}
                              <ButtonSecondary
                                text={t("accounting.views.ticket")}
                                onClick={() => onDownloadTicket(transaction)}
                                size="small"
                              />
                              <a
                                onClick={() => {
                                  onDelete(transaction);
                                }}
                                href="#"
                                className="text-red-600 hover:text-red-900"
                              >
                                <FontAwesomeIcon icon={faTrashCan} />
                              </a>
                            </div>
                          }
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default TransactionsTableProps;
